import type { PatternLayer, ReliefFinish } from '../types';
import { createLayer } from '../design/defaults';

/**
 * The Core Texture Pack.
 *
 * Curated starting points for the "Import or Generate" step. A preset is just a
 * layer stack plus a couple of suggestions, so picking one and then opening the
 * layer editor is a continuous experience rather than a mode switch — nothing
 * about a preset is locked.
 *
 * Layers are produced by a factory rather than stored as data because every
 * instance needs fresh layer ids.
 */

export type TexturePresetCategory = 'waves' | 'geometric' | 'organic' | 'feature';

export interface TexturePreset {
  id: string;
  name: string;
  description: string;
  category: TexturePresetCategory;
  tags: readonly string[];
  /** Relief depth in millimetres that shows the preset at its best. */
  suggestedDepth: number;
  /** Finish overrides applied together with the layers. */
  finish?: Partial<ReliefFinish>;
  build(): PatternLayer[];
}

export const TEXTURE_PACK: readonly TexturePreset[] = [
  // --- Waves ---------------------------------------------------------------
  {
    id: 'soft-waves',
    name: 'Soft Waves',
    description: 'Gentle sine waves, one crest per panel. The calmest thing in the pack.',
    category: 'waves',
    tags: ['seamless', 'calm', 'classic'],
    suggestedDepth: 8,
    build: () => [createLayer('waves', { params: { profile: 'sine', wavelength: 1, angle: 0 } })],
  },
  {
    id: 'ocean-swell',
    name: 'Ocean Swell',
    description: 'Long swells wandering under a noise field, the way real water never repeats.',
    category: 'waves',
    tags: ['seamless', 'organic', 'flowing'],
    suggestedDepth: 10,
    build: () => [
      createLayer('dunes', {
        params: {
          profile: 'sine',
          wavelength: 1.5,
          turbulence: 0.45,
          turbulenceScale: 4,
          drift: 0.35,
          seed: 7,
        },
      }),
      createLayer('organic', {
        params: { scale: 0.5, octaves: 3, warp: 0.4, seed: 91 },
        blend: 'overlay',
        amount: 0.25,
      }),
    ],
  },
  {
    id: 'interference',
    name: 'Interference',
    description: 'Two wave trains crossing at right angles, building a quilted lattice of peaks.',
    category: 'waves',
    tags: ['seamless', 'lattice', 'rhythmic'],
    suggestedDepth: 9,
    build: () => [
      createLayer('waves', { params: { profile: 'sine', wavelength: 0.5, angle: 0 } }),
      createLayer('waves', {
        params: { profile: 'sine', wavelength: 0.5, angle: 90 },
        blend: 'average',
      }),
    ],
  },
  {
    id: 'chevron',
    name: 'Chevron',
    description: 'Crisp faceted V-shapes. Prints without supports at almost any depth.',
    category: 'waves',
    tags: ['seamless', 'faceted', 'architectural'],
    suggestedDepth: 10,
    build: () => [
      createLayer('chevron', {
        params: { profile: 'triangle', wavelength: 0.5, zigSpan: 1, amplitude: 1, angle: 0 },
      }),
    ],
  },
  {
    id: 'pond-rings',
    name: 'Pond Rings',
    description: 'Concentric rings from a single point — a feature motif for the whole wall.',
    category: 'waves',
    tags: ['radial', 'multi-panel', 'focal'],
    suggestedDepth: 7,
    build: () => [
      createLayer('ripple', {
        params: { profile: 'sine', wavelength: 0.4, centerX: 0.5, centerY: 0.5, falloff: 0.3 },
      }),
    ],
  },

  // --- Geometric -----------------------------------------------------------
  {
    id: 'pyramid-grid',
    name: 'Pyramid Grid',
    description: 'Four sharp pyramids per panel. Hard shadows, unmistakable rhythm.',
    category: 'geometric',
    tags: ['lattice', 'faceted', 'support-free'],
    suggestedDepth: 12,
    build: () => [createLayer('pyramids', { params: { shape: 'pyramid', cell: 0.5 } })],
  },
  {
    id: 'diamond-quilt',
    name: 'Diamond Quilt',
    description: 'Staggered diamonds with softened flanks — upholstery rendered in plastic.',
    category: 'geometric',
    tags: ['lattice', 'soft', 'upholstery'],
    suggestedDepth: 8,
    finish: { contrast: 0.25 },
    build: () => [
      createLayer('pyramids', {
        params: { shape: 'diamond', cell: 0.5, stagger: 0.5, sharpness: 0.65 },
      }),
    ],
  },
  {
    id: 'acoustic-slats',
    name: 'Acoustic Slats',
    description: 'Rounded vertical battens on a flat land. The contemporary slat-wall look.',
    category: 'geometric',
    tags: ['linear', 'architectural', 'seamless'],
    suggestedDepth: 12,
    build: () => [
      createLayer('ribs', {
        params: { spacing: 0.25, width: 0.65, angle: 90, reliefShape: 'round' },
      }),
    ],
  },
  {
    id: 'stacked-stone',
    name: 'Stacked Stone',
    description: 'Offset courses with recessed joints and slightly varying depths.',
    category: 'geometric',
    tags: ['lattice', 'masonry', 'natural'],
    suggestedDepth: 9,
    build: () => [
      createLayer('bricks', {
        params: {
          length: 1,
          height: 0.25,
          stagger: 0.5,
          joint: 0.07,
          bevel: 0.16,
          variation: 0.35,
          seed: 4,
        },
      }),
      createLayer('organic', {
        params: { scale: 0.25, octaves: 4, ridged: true, warp: 0.3, seed: 66 },
        blend: 'multiply',
        amount: 0.3,
      }),
    ],
  },
  {
    id: 'bubble-wall',
    name: 'Bubble Wall',
    description: 'Touching spherical caps in a staggered field. Soft light, no hard edges.',
    category: 'geometric',
    tags: ['lattice', 'soft', 'playful'],
    suggestedDepth: 10,
    build: () => [createLayer('domes', { params: { cell: 0.375, stagger: 0.5, coverage: 1.05 } })],
  },
  {
    id: 'basket-weave',
    name: 'Basket Weave',
    description: 'Interlaced strands running over and under, continuing across every joint.',
    category: 'geometric',
    tags: ['lattice', 'textile', 'seamless'],
    suggestedDepth: 7,
    build: () => [createLayer('weave', { params: { strand: 0.25, depth: 0.45, gap: 0.06 } })],
  },
  {
    id: 'low-poly',
    name: 'Low Poly',
    description: 'Flat triangular facets catching the light at every angle.',
    category: 'geometric',
    tags: ['faceted', 'crystalline', 'seamless'],
    suggestedDepth: 11,
    build: () => [createLayer('facets', { params: { cell: 0.5, relief: 0.9, seed: 13 } })],
  },

  // --- Organic -------------------------------------------------------------
  {
    id: 'organic-flow',
    name: 'Organic Flow',
    description: 'Warped fractal relief that reads as wood grain or poured stone.',
    category: 'organic',
    tags: ['noise', 'natural', 'seamless'],
    suggestedDepth: 8,
    finish: { contrast: 0.2 },
    build: () => [
      createLayer('organic', {
        params: { scale: 1.5, octaves: 5, gain: 0.5, warp: 0.55, warpScale: 3, seed: 42 },
      }),
    ],
  },
  {
    id: 'river-stones',
    name: 'River Stones',
    description: 'Rounded pebbles packed edge to edge, each one a little different.',
    category: 'organic',
    tags: ['cellular', 'natural', 'seamless'],
    suggestedDepth: 9,
    build: () => [
      createLayer('cells', { params: { cell: 0.3, mode: 'pebbles', sharpness: 0.8, seed: 5 } }),
    ],
  },
  {
    id: 'strata',
    name: 'Strata',
    description: 'Stretched contour bands, like a cliff face read as a topographic map.',
    category: 'organic',
    tags: ['contour', 'layered', 'seamless'],
    suggestedDepth: 7,
    build: () => [
      createLayer('flow', { params: { scale: 2.5, strength: 4, bands: 11, angle: 0, seed: 23 } }),
    ],
  },
  {
    id: 'desert-dunes',
    name: 'Desert Dunes',
    description: 'Asymmetric drifts with crests that fade in and out along their length.',
    category: 'organic',
    tags: ['wave', 'noise', 'seamless'],
    suggestedDepth: 11,
    build: () => [
      createLayer('dunes', {
        params: {
          profile: 'round',
          wavelength: 1.25,
          turbulence: 0.7,
          turbulenceScale: 3,
          drift: 0.5,
          seed: 31,
        },
      }),
    ],
  },
  {
    id: 'crackle',
    name: 'Crackle',
    description: 'Raised plates parted by fine cracks — dry earth, glaze craquelure.',
    category: 'organic',
    tags: ['cellular', 'natural', 'fine'],
    suggestedDepth: 5,
    finish: { contrast: 0.4 },
    build: () => [
      createLayer('cells', { params: { cell: 0.22, mode: 'crackle', sharpness: 1.4, seed: 19 } }),
    ],
  },

  // --- Feature -------------------------------------------------------------
  {
    id: 'starburst',
    name: 'Starburst',
    description:
      'Radial arms sweeping across the whole grid. Only makes sense on more than one panel — which is the point.',
    category: 'feature',
    tags: ['radial', 'multi-panel', 'statement'],
    suggestedDepth: 9,
    build: () => [
      createLayer('starburst', { params: { arms: 16, twist: 0.35, taper: 0.45, profile: 'sine' } }),
    ],
  },
  {
    id: 'topography',
    name: 'Topography',
    description: 'Contour lines over rolling terrain: a map of a place that does not exist.',
    category: 'feature',
    tags: ['contour', 'multi-panel', 'detailed'],
    suggestedDepth: 8,
    build: () => [
      createLayer('organic', { params: { scale: 2.5, octaves: 4, warp: 0.3, seed: 77 } }),
      createLayer('flow', {
        params: { scale: 3, strength: 1.5, bands: 14, seed: 77 },
        blend: 'overlay',
        amount: 0.55,
      }),
    ],
  },
  {
    id: 'fading-ribs',
    name: 'Fading Ribs',
    description:
      'Fluting that dissolves across the wall — a gradient layer multiplied over the ribs.',
    category: 'feature',
    tags: ['linear', 'multi-panel', 'gradient'],
    suggestedDepth: 12,
    build: () => [
      createLayer('ribs', {
        params: { spacing: 0.2, width: 0.7, angle: 90, reliefShape: 'round' },
      }),
      createLayer('gradient', {
        params: { mode: 'linear', angle: 0, start: 0.05, end: 1, ease: true },
        blend: 'multiply',
      }),
    ],
  },
];

export function getPreset(id: string): TexturePreset | undefined {
  return TEXTURE_PACK.find((preset) => preset.id === id);
}

export function presetsByCategory(category: TexturePresetCategory): TexturePreset[] {
  return TEXTURE_PACK.filter((preset) => preset.category === category);
}

export const PRESET_CATEGORIES: ReadonlyArray<{ id: TexturePresetCategory; label: string }> = [
  { id: 'waves', label: 'Waves' },
  { id: 'geometric', label: 'Geometric' },
  { id: 'organic', label: 'Organic' },
  { id: 'feature', label: 'Feature' },
];
